import React from "react";
import { Doughnut } from "react-chartjs-2";

import { Checkbox, Text } from "@performyard/py-components";

import type { Goal } from "../../../../api/services/searchServiceResultTypes/goal";

import { getProgressChartData, getProgressChartOptions } from "../utils";
import "../utils/chart-setup";

import GoalConstants from "../../../../constants/Goals";

type GoalRowDisplayProps = {
	goal: Goal;
	index: number;
	isSelected?: boolean;
	isDisabled?: boolean;
	onToggle?: () => void;
	showCheckbox?: boolean;
	size?: "small" | "medium";
};

/**
 * Reusable component for displaying a goal row with progress chart and metadata.
 * Used in AddGoalsToAgendaModal and can be reused elsewhere.
 */
const GoalRowDisplay = ({
	goal,
	index,
	isSelected = false,
	isDisabled = false,
	onToggle,
	showCheckbox = true,
	size = "small",
}: GoalRowDisplayProps): React.ReactElement => {
	const statusLabel =
		GoalConstants.statusLabels?.[goal.status as keyof typeof GoalConstants.statusLabels] || goal.status;
	const categoriesLabel = goal.categories?.map((category) => category.name).join(", ");
	const chartSize = size === "small" ? 24 : 32;

	return (
		<div
			className={`flex items-center gap-2 px-3 py-2 ${index % 2 === 0 ? "bg-neutral-1" : "bg-white"}`}
			data-testid={`goal-row-${goal._id}`}
		>
			{showCheckbox && (
				<Checkbox
					id={`goal-${goal._id}`}
					checked={isSelected}
					onCheckedChange={onToggle}
					disabled={isDisabled}
					aria-label={`Select ${goal.name}`}
				/>
			)}
			<div className="flex min-w-0 flex-1 items-center gap-2">
				<div
					className="flex shrink-0 items-center justify-center"
					style={{ width: chartSize, height: chartSize }}
				>
					<Doughnut
						width={chartSize}
						options={getProgressChartOptions()}
						data={getProgressChartData(goal.progress || 0, goal.outlook?.color)}
					/>
				</div>
				<div className="min-w-0 flex-1">
					<Text size="paragraph-3" weight="semi-bold">
						{`(${statusLabel}) ${goal.name}`}
					</Text>
					<div className="flex flex-wrap items-center gap-1">
						<Text size="caption" weight="regular" color="neutral-7">
							{goal.assignee?.name}
						</Text>
						{categoriesLabel ? (
							<>
								<Text size="caption" weight="regular" color="neutral-7">
									•
								</Text>
								<Text size="caption" weight="regular" color="neutral-7">
									{`Categories: ${categoriesLabel}`}
								</Text>
							</>
						) : null}
					</div>
				</div>
			</div>
		</div>
	);
};

export default GoalRowDisplay;
